import React, { createContext, useContext, useState, useCallback } from 'react';

interface ProfileUpdateContextType {
  profileUpdateTrigger: number;
  triggerProfileUpdate: (userId?: string) => void;
  subscribeToProfileUpdates: (callback: (userId?: string) => void) => () => void;
}

const ProfileUpdateContext = createContext<ProfileUpdateContextType | undefined>(undefined);

export function ProfileUpdateProvider({ children }: { children: React.ReactNode }) {
  const [profileUpdateTrigger, setProfileUpdateTrigger] = useState(0);
  const [subscribers, setSubscribers] = useState<Array<(userId?: string) => void>>([]);

  const triggerProfileUpdate = useCallback((userId?: string) => {
    setProfileUpdateTrigger(prev => prev + 1);
    
    // Notify all subscribers
    subscribers.forEach(callback => callback(userId));
    
    console.log(`Profile update triggered for user: ${userId || 'all users'}`);
  }, [subscribers]);

  const subscribeToProfileUpdates = useCallback((callback: (userId?: string) => void) => {
    setSubscribers(prev => [...prev, callback]);
    
    // Return unsubscribe function
    return () => {
      setSubscribers(prev => prev.filter(cb => cb !== callback));
    };
  }, []);

  const value = {
    profileUpdateTrigger,
    triggerProfileUpdate,
    subscribeToProfileUpdates
  };

  return (
    <ProfileUpdateContext.Provider value={value}>
      {children}
    </ProfileUpdateContext.Provider>
  );
}

export function useProfileUpdates() {
  const context = useContext(ProfileUpdateContext);
  if (context === undefined) {
    throw new Error('useProfileUpdates must be used within a ProfileUpdateProvider');
  }
  return context;
}